import { useState, useEffect, useMemo } from 'react';
import { Plus, X, Loader2, Search, ChevronLeft, Gamepad2, SlidersHorizontal, Layers, AlertTriangle, Zap } from 'lucide-react';
import { getAllPlayables, type PlayableListItem } from '@/api/modules/playables';
import { cn } from '@/utils/cn';
import { PRESET_DIMENSIONS } from '@/components/batch-config';

// ============ Dimension types & helpers ============

export interface DimItem { name: string; values: string[]; }

/**
 * 阶段4：涉及图片重新生成的维度。
 * 选中这些维度且变体数量较多时，生图耗时显著，需二次确认。
 * 其他维度（配色/CTA/难度/音效等）均为配置级变更，秒级完成不需生图。
 */
const IMAGE_DIMENSIONS = new Set(['主题', '美术风格', '角色类型', '背景类型', '开始界面']);
/** 生图并发上限（与后端 image-gen.service.ts MAX_CONCURRENT 保持一致） */
const IMAGE_CONCURRENT_LIMIT = 5;
/** 单张生图预估耗时（秒） */
const IMAGE_COST_SECONDS = 20;
/** 变体数量超过此值且涉及生图时触发二次确认 */
const CONFIRM_VARIANT_THRESHOLD = 3;

function buildDimensionPrompt(dims: DimItem[], baseLabel?: string): string {
  if (dims.length === 0) return '';
  const lines = dims.map(d => `【${d.name}】${d.values.join('、')}`);
  const base = baseLabel ? `基于「${baseLabel}」，` : '';
  return `请${base}依据以下维度组合生成变体试玩，每个变体在对应维度上体现差异化：\n${lines.join('\n')}\n\n要求：保持核心玩法一致，仅在上述维度上做变化，每个变体需有明确的视觉区分度。`;
}

// ============ Quick presets for description ============

const QUICK_PRESETS: Array<{ label: string; text: string }> = [
  { label: '换主题+图片', text: '替换所有元素、背景、道具、角色为新的主题风格，每个变体使用不同的主题，保持核心玩法不变' },
  { label: '换元素', text: '把 item 元素替换成新的造型，每个变体使用不同风格的元素设计' },
  { label: '换CTA', text: '每个变体使用不同的 CTA 文案和按钮样式' },
  { label: '换配色', text: '每个变体使用完全不同的配色方案，包括主题色、背景渐变色、按钮色' },
  { label: '全维度差异化', text: '替换主题、配色、背景、元素、CTA文案，每个变体在所有维度上有不同组合' },
];

// ============ Create Dialog (two-step with baseline playable selection) ============

export interface CreateBatchDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (desc: string, count: number, sourcePlayableId?: string, projectId?: string, dimensions?: DimItem[]) => Promise<void>;
  initialPlayableId?: string;
}

export function CreateBatchDialog({ open, onClose, onCreate, initialPlayableId }: CreateBatchDialogProps) {
  const [step, setStep] = useState(initialPlayableId ? 2 : 1);
  const [desc, setDesc] = useState('');
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [selectedPlayable, setSelectedPlayable] = useState<PlayableListItem | null>(null);
  const [playables, setPlayables] = useState<PlayableListItem[]>([]);
  const [playableSearch, setPlayableSearch] = useState('');
  const [playablesLoading, setPlayablesLoading] = useState(false);

  // ── Dimension state ──
  const [dims, setDims] = useState<DimItem[]>([]);
  const [newDimName, setNewDimName] = useState('');
  const [newDimValues, setNewDimValues] = useState<Record<string, string>>({});
  const [dimExpanded, setDimExpanded] = useState(true);

  // ── 阶段4：生图二次确认状态 ──
  const [confirming, setConfirming] = useState(false);

  const appendToDesc = (text: string) => {
    setDesc(prev => {
      const t = prev.trim();
      const next = t ? `${t}\n${text}` : text;
      return next.slice(0, 1000);
    });
  };
  const removeFromDesc = (pattern: RegExp) => {
    setDesc(prev => prev.replace(pattern, '').replace(/\n{2,}/g, '\n').trim());
  };

  const hasDim = (name: string) => dims.some(d => d.name === name);
  const togglePreset = (name: string, examples: string[]) => {
    const exists = dims.some(d => d.name === name);
    if (exists) {
      const newDims = dims.filter(d => d.name !== name);
      setDims(newDims);
      removeFromDesc(new RegExp(`【${name}】[^\n]*\n?`, 'g'));
    } else {
      const maxCount = Math.max(1, count);
      const values = examples.slice(0, Math.min(maxCount, examples.length));
      const newDims = [...dims, { name, values }];
      setDims(newDims);
      appendToDesc(`【${name}】${values.join('、')}`);
    }
  };
  const addCustomDim = () => {
    const n = newDimName.trim();
    if (!n || hasDim(n)) { setNewDimName(''); return; }
    setDims(prev => [...prev, { name: n, values: [] }]);
    setNewDimName('');
  };
  const addDimValue = (dimName: string) => {
    const val = (newDimValues[dimName] ?? '').trim();
    if (!val) return;
    const oldDim = dims.find(d => d.name === dimName);
    const newValues = oldDim ? [...oldDim.values, val] : [val];
    setDims(prev => prev.map(d => d.name === dimName ? { ...d, values: newValues } : d));
    // 同步输入框：更新该维度的指令行（push 新值）
    setDesc(prev => {
      const without = prev.replace(new RegExp(`【${dimName}】[^\n]*\n?`, 'g'), '').replace(/\n{2,}/g, '\n').trim();
      const line = `【${dimName}】${newValues.join('、')}`;
      return without ? `${without}\n${line}` : line;
    });
    setNewDimValues(prev => ({ ...prev, [dimName]: '' }));
  };
  const removeDimValue = (dimName: string, idx: number) => {
    const oldDim = dims.find(d => d.name === dimName);
    if (!oldDim) return;
    const newValues = oldDim.values.filter((_, i) => i !== idx);
    setDims(prev => prev.map(d => d.name === dimName ? { ...d, values: newValues } : d));
    setDesc(prev => {
      const without = prev.replace(new RegExp(`【${dimName}】[^\n]*\n?`, 'g'), '').replace(/\n{2,}/g, '\n').trim();
      if (newValues.length === 0) return without;
      const line = `【${dimName}】${newValues.join('、')}`;
      return without ? `${without}\n${line}` : line;
    });
  };
  const removeDim = (name: string) => {
    setDims(prev => prev.filter(d => d.name !== name));
    removeFromDesc(new RegExp(`【${name}】[^\n]*\n?`, 'g'));
  };



  useEffect(() => {
    if (!open) return;
    setStep(initialPlayableId ? 2 : 1);
    setDesc('');
    setCount(3);
    setSelectedPlayable(null);
    setDims([]);
    setNewDimName('');
    setNewDimValues({});
    setDimExpanded(true);
    setConfirming(false);
    // Fetch playables for step 1（仅已发布试玩可作为基准模板）
    setPlayablesLoading(true);
    getAllPlayables({ limit: 50, status: 'published' }).then(res => {
      setPlayables(res.data);
      if (initialPlayableId) {
        const found = res.data.find(p => p.id === initialPlayableId);
        if (found) setSelectedPlayable(found);
      }
    }).catch(() => {}).finally(() => setPlayablesLoading(false));
  }, [open, initialPlayableId]);

  if (!open) return null;

  // ── 阶段4：生图耗时预估与二次确认判断 ──
  const imageDimCount = dims.filter(d => IMAGE_DIMENSIONS.has(d.name)).length;
  // 预估变体数：多维笛卡尔积截断到 count/20，单维取 count
  const estimatedVariants = Math.max(1, Math.min(count, 20));
  // 预估生图总数：每变体 × 每个图片维度约 1 张
  const estimatedImages = imageDimCount > 0 ? estimatedVariants * imageDimCount : 0;
  const needsImageConfirm = imageDimCount > 0 && (
    estimatedImages > IMAGE_CONCURRENT_LIMIT || count > CONFIRM_VARIANT_THRESHOLD
  );
  const estimatedBatches = Math.ceil(estimatedImages / IMAGE_CONCURRENT_LIMIT);
  const estimatedSeconds = estimatedImages > 0
    ? estimatedBatches * IMAGE_COST_SECONDS + estimatedVariants * 2
    : 0;
  const estimatedTimeText = estimatedSeconds >= 60
    ? `约 ${Math.floor(estimatedSeconds / 60)} 分 ${estimatedSeconds % 60} 秒`
    : `约 ${estimatedSeconds} 秒`;

  const handleCreate = async () => {
    // 基准试玩必选：去掉纯描述生成后必须基于选中的试玩生成变体
    if (!selectedPlayable) return;
    if (!desc.trim()) return;
    // 阶段4：涉及生图且变体较多时，首次点击弹二次确认，确认后才真正创建
    if (needsImageConfirm && !confirming) {
      setConfirming(true);
      return;
    }
    setLoading(true);
    try {
      await onCreate(desc.trim(), count, selectedPlayable?.id, selectedPlayable?.projectId ?? undefined, dims.length > 0 ? dims : undefined);
      setDesc('');
      setCount(3);
      setSelectedPlayable(null);
      setDims([]);
      setConfirming(false);
    } finally {
      setLoading(false);
    }
  };

  const filteredPlayables = playables.filter(p => {
    if (!playableSearch) return true;
    const q = playableSearch.toLowerCase();
    return (p.variantLabel ?? '').toLowerCase().includes(q)
      || (p.name ?? '').toLowerCase().includes(q)
      || (p.projectName ?? '').toLowerCase().includes(q);
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="glass-card flex max-h-[88vh] w-full max-w-xl flex-col p-6" onClick={(e) => e.stopPropagation()}>
        <div className="mb-5 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-neon-cyan to-neon-purple">
            <Layers size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">新建批量任务</h3>
            <p className="text-xs text-muted-foreground">
              {step === 1 ? '① 选择基准试玩' : '② 配置生成参数'}
            </p>
          </div>
          <button onClick={onClose} className="icon-btn ml-auto rounded p-1"><X size={16} /></button>
        </div>

        {step === 1 && (
          <>
            <div className="mb-3 relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input className="input-neon w-full py-2 pl-8 pr-3 text-sm" placeholder="搜索试玩..."
                value={playableSearch} onChange={e => setPlayableSearch(e.target.value)} />
            </div>
            <div className="mb-3 max-h-52 space-y-1 overflow-y-auto rounded-lg border border-border/30 bg-muted/10 p-2">
              {playablesLoading ? (
                <div className="flex items-center justify-center py-6"><Loader2 size={20} className="animate-spin text-neon-cyan" /></div>
              ) : filteredPlayables.length === 0 ? (
                <div className="py-6 text-center text-xs text-muted-foreground">暂无试玩，请先在工作台生成一个试玩</div>
              ) : (
                filteredPlayables.map(p => {
                  const isSelected = selectedPlayable?.id === p.id;
                  return (
                    <button key={p.id} onClick={() => setSelectedPlayable(isSelected ? null : p)}
                      className={cn('flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all',
                        isSelected ? 'border-primary/50 bg-primary/10' : 'border-border/30 hover:border-border/60')}
                    >
                      <span className={cn('h-3 w-3 rounded-full border-2 shrink-0', isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40')} />
                      <div className="flex-1 min-w-0">
                        <span className="block truncate text-sm text-foreground">{p.variantLabel || `V${p.version}`}</span>
                        <span className="block truncate text-[10px] text-muted-foreground">{p.projectName ?? '-'} · {typeof p.quality?.score === 'number' ? `${p.quality.score}分` : '-'}</span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
            <div className="flex justify-end gap-2 border-t border-border/50 pt-4">
              <button onClick={onClose} className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">取消</button>
              <button onClick={() => setStep(2)} disabled={!selectedPlayable}
                className="glow-btn flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-40">
                下一步 →
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <div className="flex flex-1 flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto pr-1">
            {selectedPlayable && (
              <div className="mb-4 flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-xs">
                <Gamepad2 size={14} className="text-primary" />
                <span className="text-foreground/80">基准试玩：<span className="font-medium text-foreground">{selectedPlayable.variantLabel || `V${selectedPlayable.version}`}</span></span>
                <span className="text-muted-foreground">({selectedPlayable.projectName ?? '-'})</span>
              </div>
            )}

            {/* ── Dimension selector ── */}
            <div className="mb-4">
              <button onClick={() => setDimExpanded(!dimExpanded)}
                className="mb-2 flex w-full items-center gap-2 text-xs font-medium text-foreground">
                <SlidersHorizontal size={13} className="text-neon-cyan" />
                维度配置
                {dims.length > 0 && <span className="rounded-full bg-neon-purple/20 px-1.5 py-0 text-[10px] text-neon-purple">{dims.length} 项</span>}
                <span className="ml-auto text-[10px] text-muted-foreground">{dimExpanded ? '收起' : '展开'}</span>
              </button>
              {dimExpanded && (
                <div className="space-y-2 rounded-lg border border-border/30 bg-muted/5 p-3">
                  {/* Preset dimensions */}
                  <div className="flex flex-wrap gap-1">
                    {PRESET_DIMENSIONS.map(d => (
                      <button key={d.name} onClick={() => togglePreset(d.name, d.examples)}
                        className={cn('rounded-full border px-2 py-0.5 text-[10px] transition-colors',
                          hasDim(d.name) ? 'border-neon-cyan/60 bg-neon-cyan/10 text-neon-cyan' : 'border-border/50 text-muted-foreground hover:border-border')}>
                        {d.name}
                      </button>
                    ))}
                  </div>
                  {/* Custom dimension input */}
                  <div className="flex gap-1">
                    <input className="input-neon flex-1 px-2 py-1 text-[11px]" placeholder="自定义维度名..."
                      value={newDimName} onChange={e => setNewDimName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') addCustomDim(); }} />
                    <button onClick={addCustomDim} className="icon-btn rounded p-1"><Plus size={12} /></button>
                  </div>
                  {/* Selected dimensions value editor */}
                  {dims.length > 0 && (
                    <div className="max-h-40 space-y-1.5 overflow-y-auto">
                      {dims.map(d => (
                        <div key={d.name} className="rounded-md bg-background/50 p-2">
                          <div className="mb-1 flex items-center justify-between">
                            <span className="text-[11px] font-medium text-foreground">{d.name}</span>
                            <button onClick={() => removeDim(d.name)} className="text-muted-foreground hover:text-red-400"><X size={10} /></button>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {d.values.map((v, i) => (
                              <span key={i} className="inline-flex items-center gap-0.5 rounded bg-neon-cyan/10 px-1.5 py-0.5 text-[10px] text-neon-cyan">
                                {v}
                                <button onClick={() => removeDimValue(d.name, i)} className="hover:text-red-400">×</button>
                              </span>
                            ))}
                          </div>
                          <div className="mt-1 flex gap-1">
                            <input className="input-neon flex-1 px-1.5 py-0.5 text-[10px]" placeholder="添加值..."
                              value={newDimValues[d.name] ?? ''} onChange={e => setNewDimValues(prev => ({ ...prev, [d.name]: e.target.value }))}
                              onKeyDown={e => { if (e.key === 'Enter') addDimValue(d.name); }} />
                            <button onClick={() => addDimValue(d.name)} className="icon-btn rounded p-0.5"><Plus size={10} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Quick preset commands ── */}
            <div className="mb-2 flex flex-wrap items-center gap-1">
              <Zap size={11} className="shrink-0 text-amber-400" />
              {QUICK_PRESETS.map(p => (
                <button key={p.label} onClick={() => appendToDesc(p.text)}
                  className="rounded-full border border-border/50 px-2 py-0.5 text-[10px] text-muted-foreground transition-colors hover:border-amber-400/60 hover:text-amber-400">
                  {p.label}
                </button>
              ))}
            </div>

            {/* ── Description (auto-filled from dimensions or manually edited) ── */}
            <div className="mb-4">
              <label className="mb-1.5 block text-xs font-medium text-foreground">
                生成变体描述 <span className="text-red-400">*</span>
              </label>
              <textarea
                className="input-neon w-full resize-none px-3 py-2.5 text-sm"
                placeholder={selectedPlayable
                  ? `基于「${selectedPlayable.variantLabel || `V${selectedPlayable.version}`}」生成变体...`
                  : '请先选择基准试玩'}
                rows={5}
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                maxLength={1000}
              />
              <span className="mt-1 block text-[10px] text-muted-foreground">{desc.length}/1000</span>
            </div>
            <div className="mb-4">
              <label className="mb-1.5 block text-xs font-medium text-foreground">生成数量</label>
              <input type="number" min={1} max={20} value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                className="input-neon w-24 px-3 py-2 text-center text-sm" />
              <span className="ml-2 text-[11px] text-muted-foreground">（1-20 个变体{dims.length > 0 ? '，多维时按笛卡尔积截断' : ''}）</span>
            </div>
            </div>
            {/* 阶段4：生图二次确认提示（首次点击开始后展示） */}
            {confirming && needsImageConfirm && (
              <div className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5">
                <div className="mb-1 flex items-center gap-1.5 text-xs font-medium text-amber-400">
                  <AlertTriangle size={13} />生成耗时较长，请确认
                </div>
                <div className="space-y-0.5 text-[11px] text-foreground/80">
                  <p>当前配置：{estimatedVariants} 个变体 × {imageDimCount} 个图片维度 ≈ <span className="font-medium text-amber-400">{estimatedImages} 张 AI 生图</span></p>
                  <p>生图并发上限为 {IMAGE_CONCURRENT_LIMIT} 张，将分 {estimatedBatches} 批依次生成</p>
                  <p>预计总耗时：<span className="font-medium text-amber-400">{estimatedTimeText}</span></p>
                  <p className="text-muted-foreground">生成期间变体不可预览/发布/下载，完成后自动解锁。</p>
                </div>
              </div>
            )}
            <div className="flex shrink-0 justify-end gap-2 border-t border-border/50 pt-4">
              <button onClick={() => setStep(1)} className="flex items-center gap-1 rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">
                <ChevronLeft size={14} />上一步
              </button>
              {confirming && needsImageConfirm && (
                <button onClick={() => setConfirming(false)}
                  className="rounded-lg border border-border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">
                  再想想
                </button>
              )}
              <button onClick={handleCreate} disabled={loading || !desc.trim() || !selectedPlayable}
                className="glow-btn flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-medium disabled:opacity-40">
                <Layers size={14} />
                {loading ? '创建中...' : (confirming && needsImageConfirm ? `确认生成（${estimatedTimeText}）` : '开始生成')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
